/** @format */
import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  MenuItem,
} from "@mui/material";
import {
  ContentCopy as ContentCopyIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";

import { getCurrentUserId } from "@utils/userUtils";
import {
  getFacilityStatusDashboard,
  getUserFacilities,
  type FacilityStatusDashboardResponse,
  type UserFacility,
} from "@services/facilityManagement";
import { formatFacilityDropdownLabel, SELECTED_FACILITY_STORAGE_KEY } from "@utils/facilityUtils";
import {
  buildDashboardFacilityRows,
  collectOrderedDashboardMembers,
  computeDashboardRollups,
  dashboardFacilityNumericId,
  dashboardRowsWithStatusEnforcement,
  flattenUserFacilitiesTrees,
  rollupFromApiGlobal,
  type DashboardFacilityFlat,
} from "@utils/multiFacilityDashboard";

function roleChipLabel(role: DashboardFacilityFlat["hierarchy_role"]): string {
  switch (role) {
    case "parent":
      return "Parent";
    case "child":
      return "Child";
    case "orphan_child":
      return "Child (orphan)";
    default:
      return "Standalone";
  }
}

async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
  }
}

function formatUsd(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(amount);
}

/** @internal exported for focused tests without rendering the guard */
export function MultiFacilityStatusDashboardBody(): JSX.Element {
  const navigate = useNavigate();
  const currentCalendarYear = new Date().getFullYear();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadNote, setLoadNote] = useState<string | null>(null);
  const [rows, setRows] = useState<DashboardFacilityFlat[]>([]);
  const [dashboardMeta, setDashboardMeta] =
    useState<FacilityStatusDashboardResponse | null>(null);

  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [activeOnly, setActiveOnly] = useState<"" | "active" | "inactive">("");
  const [verifiedOnly, setVerifiedOnly] = useState<
    "" | "verified" | "unverified"
  >("");
  const [profileFilter, setProfileFilter] = useState<
    "" | "approved" | "not_approved"
  >("");
  const [enfFilter, setEnfFilter] = useState<"" | "has" | "none">("");
  const [enfYearFrom, setEnfYearFrom] = useState(currentCalendarYear);
  const [enfYearTo, setEnfYearTo] = useState(currentCalendarYear);

  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [actionMenuRow, setActionMenuRow] = useState<DashboardFacilityFlat | null>(
    null,
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const userIdStr = getCurrentUserId();
        const uid = userIdStr ? parseInt(userIdStr, 10) : 0;
        if (!userIdStr || !uid || Number.isNaN(uid) || uid <= 0) {
          setError("Not signed in.");
          setLoading(false);
          return;
        }

        try {
          const dash = await getFacilityStatusDashboard(uid, {
            enforcement_year_from: enfYearFrom,
            enforcement_year_to: enfYearTo,
          });
          if (!cancelled && dash.success && dash.global_rollup) {
            const members = collectOrderedDashboardMembers(dash);
            setDashboardMeta(dash);
            setLoadNote(null);
            setRows(dashboardRowsWithStatusEnforcement(members));
            return;
          }
          if (!cancelled && dash && !dash.success) {
            setLoadNote(dash.message || null);
          }
        } catch {
          if (!cancelled) setLoadNote(null);
        }

        const resp = await getUserFacilities(uid);
        if (!cancelled) {
          setDashboardMeta(null);
          const list = flattenUserFacilitiesTrees(resp.facilities || []);
          setRows(buildDashboardFacilityRows(list));
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enfYearFrom, enfYearTo]);

  const stateOptions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => {
      const s = (r.state || "").trim().toUpperCase();
      if (s) set.add(s);
    });
    return [...set].sort();
  }, [rows]);

  const typeOptions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => {
      const ft = `${(r.facility_type as string) ?? ""}`.trim();
      if (ft) set.add(ft);
      const slug = `${(r as { facility_type_slug?: string }).facility_type_slug ?? ""}`.trim();
      const label = slug || ft;
      if (label) set.add(label);
    });
    return [...set].sort();
  }, [rows]);

  const rollups = useMemo(() => {
    if (dashboardMeta?.global_rollup) {
      return rollupFromApiGlobal(dashboardMeta.global_rollup);
    }
    return computeDashboardRollups(rows);
  }, [dashboardMeta, rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const g = groupFilter.trim().toUpperCase();
    const st = stateFilter.trim().toUpperCase();
    const tft = typeFilter.trim().toLowerCase();

    return rows.filter((r) => {
      if (g) {
        const inGroup =
          r._facility_group_id === g ||
          r._parent_facility_group_id === g ||
          r._rollup_group_key === g;
        if (!inGroup) return false;
      }
      if (st && `${r.state ?? ""}`.trim().toUpperCase() !== st) return false;
      if (tft) {
        const nameType = `${(r.facility_type as string) ?? ""}`.trim().toLowerCase();
        const slug = `${(r as { facility_type_slug?: string }).facility_type_slug ?? ""}`
          .trim()
          .toLowerCase()
          .replace(/_/g, " ");
        if (!nameType.includes(tft) && !slug.includes(tft)) return false;
      }
      if (activeOnly === "active" && r.is_active === false) return false;
      if (activeOnly === "inactive" && r.is_active !== false) return false;
      if (verifiedOnly === "verified" && r.is_verified !== true) return false;
      if (verifiedOnly === "unverified" && r.is_verified === true) return false;
      if (profileFilter === "approved") {
        if (!(r as { is_profile_approved?: boolean }).is_profile_approved)
          return false;
      }
      if (profileFilter === "not_approved") {
        if ((r as { is_profile_approved?: boolean }).is_profile_approved)
          return false;
      }

      if (dashboardMeta) {
        const ec = r._enforcement_action_count ?? 0;
        if (enfFilter === "has" && ec <= 0) return false;
        if (enfFilter === "none" && ec > 0) return false;
      }

      if (q) {
        const tokens = q.split(/\s+/).filter(Boolean);
        const blobs = [
          r.facility_name,
          r.ccn_number,
          r.organization_name,
          r.city,
          r.state,
          r._facility_group_id,
          r._parent_facility_group_id,
          (r as { primary_profile_slug?: string }).primary_profile_slug,
          (r as { vertical?: string }).vertical,
          (r.facility_type as string) ?? "",
          (r as { facility_type_slug?: string }).facility_type_slug ?? "",
        ]
          .map((x) => `${x ?? ""}`.toLowerCase())
          .join(" ");
        for (const tok of tokens) {
          if (!blobs.includes(tok)) return false;
        }
      }
      return true;
    });
  }, [
    rows,
    search,
    groupFilter,
    stateFilter,
    typeFilter,
    activeOnly,
    verifiedOnly,
    profileFilter,
    dashboardMeta,
    enfFilter,
  ]);

  useEffect(() => {
    setPage(0);
  }, [
    search,
    groupFilter,
    stateFilter,
    typeFilter,
    activeOnly,
    verifiedOnly,
    profileFilter,
    enfFilter,
    enfYearFrom,
    enfYearTo,
  ]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  const drillNavigate = useCallback(
    (
      facility: UserFacility,
      fid: number,
      activeTab?: "compliance" | "penalty" | "riskanalysis" | "facilities",
    ) => {
      try {
        sessionStorage.setItem(
          SELECTED_FACILITY_STORAGE_KEY,
          JSON.stringify(facility),
        );
      } catch {
        /* ignore storage failures */
      }
      navigate("/dashboard/facilities", {
        state: {
          currentView: "MyFacility",
          facilityId: fid,
          ...(activeTab ? { activeTab } : {}),
        },
      });
    },
    [navigate],
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const summarySx = {
    flex: "1 1 140px",
    minWidth: 140,
    borderRadius: 2,
    border: "1px solid #e1e8ed",
    bgcolor: "#fafbfc",
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, maxWidth: 1400, mx: "auto" }}>
      <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: "#065B70" }}>
        Multi-Facility Status
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Read-only rollup of your associated facilities grouped by{" "}
        <strong>facility_group_id</strong> (PF share code). Data loads from the facility status
        dashboard API when available (with persisted enforcement totals for the selected years);
        otherwise the page falls back to user facilities only. Actions open{" "}
        <strong>My Facility</strong> with the facility stored for the Hub shell.
      </Typography>

      {loadNote && !dashboardMeta ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {loadNote}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mb: 2,
        }}
      >
        <Card sx={summarySx} variant="outlined">
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              Total
            </Typography>
            <Typography variant="h6">{rollups.total}</Typography>
          </CardContent>
        </Card>
        <Card sx={summarySx} variant="outlined">
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              Active
            </Typography>
            <Typography variant="h6">{rollups.active}</Typography>
          </CardContent>
        </Card>
        <Card sx={summarySx} variant="outlined">
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              Verified
            </Typography>
            <Typography variant="h6">{rollups.verified}</Typography>
          </CardContent>
        </Card>
        <Card sx={summarySx} variant="outlined">
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              Profile not approved
            </Typography>
            <Typography variant="h6">{rollups.profileNotApproved}</Typography>
          </CardContent>
        </Card>
        <Card sx={summarySx} variant="outlined">
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              Medicare cert gap
            </Typography>
            <Typography variant="h6">{rollups.medicareCertGap}</Typography>
          </CardContent>
        </Card>
        <Card sx={summarySx} variant="outlined">
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              Children needing attention
            </Typography>
            <Typography variant="h6">{rollups.childNeedingAttention}</Typography>
          </CardContent>
        </Card>
        <Card sx={summarySx} variant="outlined">
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              Orphan child rows
            </Typography>
            <Typography variant="h6">{rollups.orphanChildren}</Typography>
          </CardContent>
        </Card>
        {dashboardMeta?.global_rollup != null ? (
          <>
            <Card sx={summarySx} variant="outlined">
              <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">
                  Enforcement actions (
                  {dashboardMeta.enforcement_year_from}–
                  {dashboardMeta.enforcement_year_to})
                </Typography>
                <Typography variant="h6">
                  {dashboardMeta.global_rollup.enforcement_action_count}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={summarySx} variant="outlined">
              <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">
                  Enforcement exposure (persisted snapshots)
                </Typography>
                <Typography variant="h6">
                  {formatUsd(dashboardMeta.global_rollup.enforcement_total_amount)}
                </Typography>
              </CardContent>
            </Card>
          </>
        ) : null}
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        {dashboardMeta
          ? `Enforcement totals use persisted Rules snapshots for ${dashboardMeta.country_code}, calendar years ${dashboardMeta.enforcement_year_from}–${dashboardMeta.enforcement_year_to}.`
          : "Facility status dashboard API did not return a rollup; enforcement columns are unavailable for this session."}
      </Alert>

      <Paper elevation={0} sx={{ p: 1.5, mb: 2, border: "1px solid #e1e8ed", borderRadius: 2 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
          <TextField
            size="small"
            label="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: "1 1 200px", minWidth: 200 }}
          />
          <TextField
            size="small"
            label="Parent group ID (PF-…)"
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value.toUpperCase())}
            sx={{ width: 200 }}
          />
          <TextField size="small" select label="State" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} sx={{ width: 110 }}>
            <MenuItem value="">All</MenuItem>
            {stateOptions.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <TextField size="small" select label="Facility type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">All</MenuItem>
            {typeOptions.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
          <TextField size="small" select label="Active" value={activeOnly} onChange={(e) => setActiveOnly(e.target.value as typeof activeOnly)} sx={{ width: 120 }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          <TextField size="small" select label="Verified" value={verifiedOnly} onChange={(e) => setVerifiedOnly(e.target.value as typeof verifiedOnly)} sx={{ width: 130 }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="verified">Verified</MenuItem>
            <MenuItem value="unverified">Unverified</MenuItem>
          </TextField>
          <TextField size="small" select label="Profile" value={profileFilter} onChange={(e) => setProfileFilter(e.target.value as typeof profileFilter)} sx={{ width: 150 }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="not_approved">Not approved</MenuItem>
          </TextField>
          <TextField
            size="small"
            type="number"
            label="Enf. year from"
            value={enfYearFrom}
            onChange={(e) =>
              setEnfYearFrom(parseInt(e.target.value, 10) || currentCalendarYear)
            }
            sx={{ width: 130 }}
            inputProps={{ min: 1990, max: 2100 }}
          />
          <TextField
            size="small"
            type="number"
            label="Enf. year to"
            value={enfYearTo}
            onChange={(e) =>
              setEnfYearTo(parseInt(e.target.value, 10) || currentCalendarYear)
            }
            sx={{ width: 120 }}
            inputProps={{ min: 1990, max: 2100 }}
          />
          <TextField
            size="small"
            select
            label="Enforcement filter"
            value={enfFilter}
            onChange={(e) => setEnfFilter(e.target.value as typeof enfFilter)}
            sx={{ width: 165 }}
            disabled={!dashboardMeta}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="has">Has actions</MenuItem>
            <MenuItem value="none">No actions</MenuItem>
          </TextField>
        </Box>
      </Paper>

      <TableContainer component={Paper} sx={{ border: "1px solid #e1e8ed", borderRadius: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Role</TableCell>
              <TableCell>Group ID</TableCell>
              <TableCell>Parent group ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Vertical</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Verified</TableCell>
              <TableCell>Profile OK</TableCell>
              <TableCell>Medicare</TableCell>
              <TableCell align="right">Enf. count</TableCell>
              <TableCell align="right">Enf. amount</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedRows.map((r) => {
              const fid = dashboardFacilityNumericId(r as UserFacility) ?? r._fid;
              const gid = r._facility_group_id || "—";
              const pgid = r._parent_facility_group_id || "—";
              const profOk = Boolean(
                (r as { is_profile_approved?: boolean }).is_profile_approved,
              );
              const reqMed = Boolean(
                (r as { requires_medicare_certification?: boolean })
                  .requires_medicare_certification,
              );
              const cert = (r as { medicare_certification_date?: string | null })
                .medicare_certification_date;
              const medGap = reqMed && !String(cert ?? "").trim();
              const enfCount = r._enforcement_action_count;
              const enfAmt = r._enforcement_total_amount;

              return (
                <TableRow key={fid} hover>
                  <TableCell>
                    <Chip size="small" label={roleChipLabel(r.hierarchy_role)} />
                  </TableCell>
                  <TableCell>
                    {gid !== "—" ? (
                      <Button
                        size="small"
                        endIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                        onClick={() => void copyToClipboard(gid)}
                      >
                        {gid}
                      </Button>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>{pgid}</TableCell>
                  <TableCell>{formatFacilityDropdownLabel(r as UserFacility)}</TableCell>
                  <TableCell>
                    {(r.facility_type as string) ||
                      (r as { facility_type_slug?: string }).facility_type_slug ||
                      "—"}
                  </TableCell>
                  <TableCell>{(r as { vertical?: string }).vertical || "—"}</TableCell>
                  <TableCell>{r.state || "—"}</TableCell>
                  <TableCell>{r.is_active === false ? "No" : "Yes"}</TableCell>
                  <TableCell>{r.is_verified === true ? "Yes" : "No"}</TableCell>
                  <TableCell>{profOk ? "Yes" : "No"}</TableCell>
                  <TableCell>
                    {reqMed ? (medGap ? "Gap" : "OK") : "—"}
                  </TableCell>
                  <TableCell align="right">
                    {enfCount !== undefined ? enfCount : "—"}
                  </TableCell>
                  <TableCell align="right">
                    {enfAmt !== undefined ? formatUsd(enfAmt) : "—"}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      aria-label="Row actions"
                      onClick={(e) => {
                        setActionMenuAnchor(e.currentTarget);
                        setActionMenuRow(r);
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={() => {
          setActionMenuAnchor(null);
          setActionMenuRow(null);
        }}
      >
        <MenuItem
          onClick={() => {
            const row = actionMenuRow;
            const fid =
              row != null
                ? dashboardFacilityNumericId(row as UserFacility) ?? row._fid
                : null;
            setActionMenuAnchor(null);
            setActionMenuRow(null);
            if (row != null && fid != null && fid > 0) {
              drillNavigate(row as UserFacility, fid);
            }
          }}
        >
          My Facility…
        </MenuItem>
        <MenuItem
          onClick={() => {
            const row = actionMenuRow;
            const fid =
              row != null
                ? dashboardFacilityNumericId(row as UserFacility) ?? row._fid
                : null;
            setActionMenuAnchor(null);
            setActionMenuRow(null);
            if (row != null && fid != null && fid > 0) {
              drillNavigate(row as UserFacility, fid, "compliance");
            }
          }}
        >
          Continuous compliance
        </MenuItem>
        <MenuItem
          onClick={() => {
            const row = actionMenuRow;
            const fid =
              row != null
                ? dashboardFacilityNumericId(row as UserFacility) ?? row._fid
                : null;
            setActionMenuAnchor(null);
            setActionMenuRow(null);
            if (row != null && fid != null && fid > 0) {
              drillNavigate(row as UserFacility, fid, "penalty");
            }
          }}
        >
          Penalties
        </MenuItem>
        <MenuItem
          onClick={() => {
            const row = actionMenuRow;
            const fid =
              row != null
                ? dashboardFacilityNumericId(row as UserFacility) ?? row._fid
                : null;
            setActionMenuAnchor(null);
            setActionMenuRow(null);
            if (row != null && fid != null && fid > 0) {
              drillNavigate(row as UserFacility, fid, "riskanalysis");
            }
          }}
        >
          Risk analysis
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default function MultiFacilityStatusPage(): JSX.Element {
  return <MultiFacilityStatusDashboardBody />;
}
